import { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { API } from "aws-amplify";
import { PortfolioPositionsTable } from "./PortfolioPositionsTable";
import { pricedBondState, userDataState } from "../../../../state";
import { doPortfolioCustomListPositions } from "../../../backendApi/amplifyGraphQl/PortfolioCustomListPositions/doPortfolioCustomListPositions";
import { CorporateBondId, OrderDirection, TradeTicketId } from "@goodfill/domain-properties";
import { PresentedPortfolioPosition } from "./PresentedPortfolioPosition";
import { useRecoilValue } from "recoil";
import { PortfolioPositionTradeTicket } from "../../../backendApi/amplifyGraphQl/PortfolioCustomListPositions/PortfolioTradeTicket";
import { mapPortfolioTradeTicketToPresentedPosition } from "../../../backendApi/amplifyGraphQl/PortfolioCustomListPositions/mapPortfolioTradeTicketToPresentedPosition";
import { SelectedToBatchSellContext } from "./batchSell/SelectedToBatchSellContext";
import { ProgressBar, Intent } from "@blueprintjs/core";

export const PortfolioPositions = ({ bondId }: { bondId?: CorporateBondId }) => {
  const userData = useRecoilValue(userDataState);

  const accountId = userData?.account.id;
  const [isLoading, setIsLoading] = useState(false);
  const pricedBonds = useRecoilValue(pricedBondState);
  const [positions, setPositions] = useState<PortfolioPositionTradeTicket[]>([]);

  const [selectedTickets, setSelectedTickets] = useState<TradeTicketId[]>([]);
  const [isSellDialogOpen, setIsSellDialogOpen] = useState(false);

  const init = useCallback(async () => {
    if (!accountId) return;

    try {
      setIsLoading(true);
      const result = await doPortfolioCustomListPositions(API, accountId);
      setPositions(result);
    } catch (e) {
      console.error(e);
      toast.error(`Loading Trade Tickets failed ${e}`);
    } finally {
      setIsLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    init();
  }, [init]);

  if (bondId === null) {
    return <></>;
  }

  const rows = positions
    .filter(
      (position) =>
        (bondId === undefined || position.orderTicket.bondId === bondId) &&
        position.orderTicket.direction === OrderDirection.Buy &&
        position.alreadySold.lessThan(position.amount),
    )
    .reduce<PresentedPortfolioPosition[]>((acc, tradeTicket) => {
      const bond = pricedBonds.get(tradeTicket.orderTicket.bondId);
      if (bond != null) {
        acc.push(mapPortfolioTradeTicketToPresentedPosition(tradeTicket, bond));
      }

      return acc;
    }, []);

  const onRowRemove = (rowId: TradeTicketId) => {
    setSelectedTickets((selectedIds) => selectedIds.filter((selectedId) => selectedId !== rowId));
  };

  return (
    <div>
      <SelectedToBatchSellContext.Provider
        value={{
          isDialogOpen: isSellDialogOpen,
          selected: selectedTickets,
          setIsDialogOpen: setIsSellDialogOpen,
          setSelected: setSelectedTickets,
          onRowRemove,
        }}
      >
        {isLoading ? <ProgressBar intent={Intent.PRIMARY} /> : <div className="h-[8px]" />}
        <PortfolioPositionsTable positionRows={rows} />
      </SelectedToBatchSellContext.Provider>
    </div>
  );
};
